import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Constants } from '@constants';
import { GridFilter } from '@fs/components/base/grid-base.component';
import { Grid } from '@fs/enums/grid.enum';
import { EstimateGridModel } from '@fs/models/estimate/estimate-grid.model';
import { DataSharingLTE } from '@fs/services/data-sharing-lte.service';
import { EstimateService } from '@fs/services/estimate/estimate.service';
import { CommonMethods } from '@services/common.method';
import { TabOpenService } from '@services/tab-open.service';
import { GridAction, GridData } from '@shared/models/grid.model';
import { ExportService } from '@shared/services/export.service';
import { of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { ExportType } from "@fs/enums/common-enums";
import { MapExportData } from '@shared/data/MapExportData';
import { ExportColumnHeaders } from '@shared/models/export-headers.model';
import { DatePipe } from '@angular/common';
import { TabParams } from '@fs/services/tab-params.service';
import { PermissionPipe } from '@shared/pipes/permission';
import { ActionName } from '@shared/enums/action-name';
import { Tabs } from "src/app/enums/tab.enum";

export enum EstimateAction {
  ViewEstimate = "View Estimate",
  EditEstimate = "Edit Estimate"
};

@Component({
  selector: 'estimate-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  providers: [EstimateService, DatePipe]
})
export class EstimateListComponent extends GridFilter implements OnInit {
  @ViewChild('addressTemplate') addressTempRef: TemplateRef<any>;
  @ViewChild('displaynameTemplate') displaynameTempRef: TemplateRef<any>;
  gridData: GridData<EstimateGridModel> = { Data: [], Count: 0 };

  constructor(private estimateServ: EstimateService, private tabOpenServ: TabOpenService, private exportServ: ExportService,
    private commonMethod: CommonMethods, private datePipe: DatePipe, private dataShareServ: DataSharingLTE,
    private permissionPipe: PermissionPipe) {
    super(Grid.Estimate);
    this.subscribers();
   }

   ngOnInit() {
    this.setPageMetaData();
    this.setGridMetaData();
    this.getData();
  }
    
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  subscribers() {
    this.makeRequest$.pipe(
      debounceTime(Constants.debounceInterval),
      switchMap((val:string) => 
        this.estimateServ.GetEstimates(this.gridState, val).pipe(
          catchError(() => {
            return of({ Data: [], Count: 0 });
          })
      )), 
      takeUntil(this.ngUnsubscribe))
      .subscribe(r => {
        this.gridLoader = false;
        r.Data.map(x => { 
          x['EditCondition'] = null;
          if (x.AppointmentStatus && x.AppointmentSubStatus) { 
            x.AppointmentStatus = this.commonMethod.GetHelperStatus(x.AppointmentStatus.ID, x.AppointmentSubStatus.ID);
            x.EditCondition = x.AppointmentSubStatus.Code;
          };
          if (x.Status) x.EditCondition = x.Status.Code; 
          const promisedDate = this.datePipe.transform(x.PromisedDate, "MM/dd/yyyy");
          if (x.StartTime) {
            x.StartTime = this.datePipe.transform(new Date(promisedDate + " " + x.StartTime), "h:mm a");
          }
          x.JobSite['CustomerID'] = x.CustomerID;
        });
        if (this.isExport) { this.mapExportData(r) } else { this.gridData = r };
      });
  }

  setPageMetaData() {
    this.pageMetaData = {
      title: "Manage Estimates",
      placeHolder: "Estimate No., Display Name"
    }
  }

  setGridMetaData() {
    this.gridColumns = [
      { name: "Estimate No", dataKey: ['EstimateNo'], sortable: true, width: 80, minWidth: 80 },
      { name: 'Description', field:'Description', dataKey: ['Description'], sortable: true, width: 120, minWidth: 120 },
      { name: "Job Site Display Name", field: "JobSite.DisplayName", dataKey: ['JobSite'], sortable: true, width: 110, minWidth: 110, template: this.displaynameTempRef },
      { name: "Job Site Address", field: "JobSite.StreetNo", dataKey: ['JobSite'], sortable: true, width: 130, minWidth: 130, template: this.addressTempRef },
      { name: "Create Date", dataKey: ['CreateDate'], type: "date", sortable: true, width: 60, minWidth: 60 },
      { name: "Expiration Date", dataKey: ['ExpirationDate'], sortable: true, type: "date", width: 60, minWidth: 60 },
      { name: "Total", dataKey:['Total'],  type: 'currency', sortable: true, width: 40, minWidth: 40},
      { name: "State", field: "Status", dataKey: ['Status', 'Name'], sortable: true, width: 60, minWidth: 60 },
      { name: "Technician", field: "Technician", dataKey: ['Technician'], sortable: true, width: 60, minWidth: 60 },
      { name: "Date", field: "PromisedDate", dataKey: ['PromisedDate'], type: "date", sortable: true, width: 60, minWidth: 60 },
      { name: "Time", field: "StartTime", dataKey: ['StartTime'], sortable: true, width: 60, minWidth: 60 },
      { name: "Appointment State", field: "AppointmentStatus", dataKey: ['AppointmentStatus', 'Name'], sortable: true, width: 80, minWidth: 80 },
    ];
    this.gridActions = [
      { type: EstimateAction.ViewEstimate },
      { type: EstimateAction.EditEstimate, condition: { column: 'EditCondition', value: ['techReceived', 'working', 'Converted'] } }
    ]
  }

  applyFilter(filterSetting: { applyAdvanceFilter: boolean, advanceFilterActive: any } = { advanceFilterActive: true, applyAdvanceFilter: null }, isStateReset: boolean = false) {
    let filterValues = this.filterForm.controls['searchFilters'];
    let filters = jQuery.extend(true, {}, filterValues);
    const status_Completed = this.commonMethod.getCodeTypeData("WorkTicketStatus").find(x => x.Code == Constants.WOStaticStatus().completed);
    filters.value.map((x, i) => {
      if (x.field == "AppointmentSubStatusID") {
        const index = x.value.findIndex(x => x.ID === status_Completed.ID);
        if (index > -1) {
          x.value.splice(index, 1);
          filters.value.push({ field: "AppointmentStatusID", operator: x.operator, value: [status_Completed.ID] });
        }
        if (x.value.length == 0) {
          filters.value.splice(i, 1);
        }
      } else if (x.field == "EstimateStatusID") {
        x.field = "StatusID";
      } 
    });
    this.filter(filterSetting, filters);
  }

  onRowAction(e: GridAction) {
    switch(e.type) {
      case EstimateAction.ViewEstimate:
        let params = Object.assign({}, e.data);
        params["Customer"] = { ID: params["CustomerID"] };
        params["JobSiteID"]= params["JobSite"].ID;
        this.tabOpenServ.OpenTab({
          tabId: Tabs.EstimateDetail,
          params: params,
          tabTitle: params["EstimateNo"]== null ? " " : params["EstimateNo"],
          replaceIfOpen: {
                replaceTab: true,
                tabToReplace: Tabs.EstimateEdit,
                action: "focus"
          }
        });
        break;
      case EstimateAction.EditEstimate:
        if (this.permissionPipe.transform(ActionName.EditEstimate)){
        params = Object.assign({}, e.data);
        params["Customer"] = { ID: params["CustomerID"] };
        this.tabOpenServ.OpenTab({
          tabId: Tabs.EstimateEdit,
          params: params,
          tabTitle: params["EstimateNo"]== null ? " " : params["EstimateNo"],
          replaceIfOpen: {
                replaceTab: true,
                tabToReplace: Tabs.EstimateDetail,
                action: "replace"
          }
        });
        }
        break;
      default:
        break;
    }
  }

  //#region private methods 
  private async mapExportData(r) {
    this.isExport = false;
    if (this.exportType == ExportType.Excel) {
      this.exportServ.ExportToExcel({
        exportData: r.Data.map(x => MapExportData.mapEstimate(x)),
        exportHeadersColumns: ExportColumnHeaders.EstimateColumns,
        columnHeaderNotToBeIncluded: []
      });
    } else if (this.exportType == ExportType.PDF) {
      this.exportServ.ExportToPDF({
        exportData: r.Data.map(x => MapExportData.mapEstimate(x)),
        exportHeadersColumns: ExportColumnHeaders.EstimateColumns,
        columnHeaderNotToBeIncluded: []
      });
    } else if (this.exportType == ExportType.CSV) {
      this.exportServ.ExportToCSV({
        exportData: r.Data.map(x => MapExportData.mapEstimate(x)),
        exportHeadersColumns: ExportColumnHeaders.EstimateColumns,
        columnHeaderNotToBeIncluded: []
      });
    }
  }
  //#endregion
}
